import React, { useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarDays, Lock, Pencil, Trash2, Check, Plus, Clock, User, X, Send, HelpCircle } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BURGUNDY_TINT, BG, NEUT_BG, BAD_INK, OK_BG, OK_INK, WARN_BG, WARN_INK, INFO_BG, INFO_INK } from "../Bureau/bureauTokens";
import { useEtatGlobal } from "../EtatGlobal.js";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog.js";
import UserSearchTagPicker from "../../components/ui/UserSearchTagPicker";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import feuilleTempsTutorial from "../Tutoriel/FeuilleTempsTutorial";
import MiniCalendar from "./MiniCalendar";

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const MOIS_COURT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const CONFETTI_COLORS = ["#E8B04B", BURGUNDY, "#4B9E7F", "#4B7FE8", "#E86A6A", "#B04BE8", "#E8894B"];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayIso() {
  return isoOf(new Date());
}

const fieldInput = "h-9 px-3 text-xs outline-none font-semibold placeholder:font-semibold placeholder:text-slate-400";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

// Recherche + sélection d'un client (réutilisée pour la ligne d'ajout et
// l'édition inline). Interroge directement /api/clients/search (résultat
// léger : id/nom uniquement, 25 max) plutôt que le useClients() partagé de
// Portefeuille — celui-ci charge tous les clients avec 6 relations imbriquées
// (KYC, comptes, todos...) pour la grille du Portefeuille, ce qui en fait
// une requête de plusieurs secondes largement inutile pour un simple champ
// de sélection.
function ClientPicker({ value, onChange, placeholder }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      httpClient
        .get("/api/clients/search", { params: { search: query } })
        .then(({ data }) => setMatches(Array.isArray(data) ? data.slice(0, 8) : []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  const showEmptyState = q.trim().length >= 2 && !searching && matches.length === 0;

  return (
    <div className="relative">
      <input
        value={value?.nom || q}
        onChange={(e) => {
          onChange(null);
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || "— Interne (sans dossier) —"}
        className={`w-full ${fieldInput}`}
        style={fieldStyle}
      />
      <AnimatePresence>
        {open && !value && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-1 overflow-y-auto z-20"
            style={{ top: "100%", background: "#fff", border: `1px solid ${LINE}`, borderRadius: 8, boxShadow: "0 10px 28px rgba(0,0,0,.10)", maxHeight: 180 }}
          >
            <button
              onMouseDown={() => {
                onChange(null);
                setQ("");
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 text-left text-xs px-3 py-2"
              style={{ color: INK_3 }}
            >
              — Interne (sans dossier) —
            </button>
            {searching && (
              <div className="px-3 py-2 text-xs" style={{ color: INK_3 }}>
                Recherche…
              </div>
            )}
            {showEmptyState && (
              <div className="px-3 py-2 text-xs" style={{ color: INK_3 }}>
                Aucun résultat trouvé
              </div>
            )}
            {matches.map((c) => (
              <button
                key={c.id}
                onMouseDown={() => {
                  onChange({ id: c.id, nom: c.nom });
                  setQ("");
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 text-left text-xs px-3 py-2"
                style={{ color: INK }}
              >
                <User size={12} style={{ color: INK_3 }} /> {c.nom}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeuilleTemps({ onBack }) {
  const {
    utilisateur,
    users: allUsers,
    feuilleTempsEntries: entries,
    setFeuilleTempsEntries: setEntries,
    joursClotures,
    loadingFeuilleTemps: isLoading,
    fetchFeuilleTemps,
  } = useEtatGlobal();

  const closedDates = useMemo(() => new Set(joursClotures), [joursClotures]);

  const [dayISO, setDayISO] = useState(todayIso());
  const [showCal, setShowCal] = useState(false);
  const [editIds, setEditIds] = useState(() => new Set());
  const [drafts, setDrafts] = useState({});
  const [newClient, setNewClient] = useState(null);
  const [newHours, setNewHours] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ heures: false, label: false });
  // Nombre d'ajouts/modifications/suppressions encore en vol côté serveur —
  // sert à bloquer "Clôturer" tant qu'une écriture optimiste n'est pas
  // confirmée en base (voir openCloseOverlay).
  const [pendingMutations, setPendingMutations] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [toast, setToast] = useState(null);
  const [closeOverlayOpen, setCloseOverlayOpen] = useState(false);
  const [notifyUserIds, setNotifyUserIds] = useState([]);
  const [closing, setClosing] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const dObj = new Date(dayISO);
  const dayLabel = `${JOURS[dObj.getDay()]} ${dObj.getDate()} ${MOIS_COURT[dObj.getMonth()]} ${dObj.getFullYear()}`;
  const isFuture = dayISO > todayIso();
  const closed = closedDates.has(dayISO);
  const editable = !closed && !isFuture;
  const dayEntries = entries.filter((e) => isoOf(new Date(e.date)) === dayISO);
  const dayTotal = dayEntries.reduce((acc, e) => acc + e.heures, 0);
  const entryDates = useMemo(() => new Set(entries.map((e) => isoOf(new Date(e.date)))), [entries]);

  const shiftDay = (n) => {
    const d = new Date(dayISO);
    d.setDate(d.getDate() + n);
    setDayISO(isoOf(d));
    setEditIds(new Set());
  };
  const pickDay = (di) => {
    setDayISO(di);
    setEditIds(new Set());
    setShowCal(false);
  };

  // Instantané : la ligne apparaît tout de suite (id temporaire) sans
  // attendre la réponse serveur ni un refetch complet ; le toast de
  // confirmation, lui, n'arrive qu'une fois la ligne réellement enregistrée.
  const addEntry = async () => {
    const h = parseFloat(newHours);
    const label = newLabel.trim();
    const heuresInvalid = Number.isNaN(h) || h <= 0;
    const labelInvalid = !label;
    if (heuresInvalid || labelInvalid) {
      setFieldErrors({ heures: heuresInvalid, label: labelInvalid });
      flash("Veuillez remplir les informations manquantes");
      return;
    }
    setFieldErrors({ heures: false, label: false });
    setError(null);
    const tempId = `temp-${Date.now()}`;
    const client = newClient;
    setEntries((prev) => [...prev, { id: tempId, date: dayISO, client, heures: h, label }]);
    setNewClient(null);
    setNewHours("");
    setNewLabel("");
    setPendingMutations((n) => n + 1);
    try {
      const { data } = await httpClient.post("/api/saisies-temps", { date: dayISO, clientId: client?.id || null, heures: h, label });
      setEntries((prev) => prev.map((e) => (e.id === tempId ? data.data : e)));
      flash("Ligne ajoutée");
    } catch (err) {
      setEntries((prev) => prev.filter((e) => e.id !== tempId));
      setError(err.response?.data?.message || "Erreur lors de l'ajout.");
      flash("Erreur lors de l'ajout");
    } finally {
      setPendingMutations((n) => Math.max(0, n - 1));
    }
  };

  const startEdit = (e) => {
    setDrafts((prev) => ({ ...prev, [e.id]: { client: e.client ? { id: e.client.id, nom: e.client.nom } : null, heures: String(e.heures), label: e.label } }));
    setEditIds((prev) => new Set(prev).add(e.id));
  };
  const cancelEdit = (id) => {
    setEditIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    setDrafts((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };
  // Instantané comme addEntry : la ligne est mise à jour dans le UI dès la
  // confirmation, avant la réponse serveur ; rollback + toast d'erreur si la
  // modification échoue en base.
  const commitEdit = async (id) => {
    const d = drafts[id];
    if (!d) return;
    const h = parseFloat(d.heures);
    if (Number.isNaN(h) || h <= 0) return;
    const label = d.label.trim();
    const client = d.client;
    const previousEntry = entries.find((e) => e.id === id);
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, client, heures: h, label } : e)));
    cancelEdit(id);
    setPendingMutations((n) => n + 1);
    try {
      const { data } = await httpClient.put(`/api/saisies-temps/${id}`, { clientId: client?.id || null, heures: h, label });
      setEntries((prev) => prev.map((e) => (e.id === id ? data.data : e)));
      flash("Ligne modifiée");
    } catch (err) {
      if (previousEntry) setEntries((prev) => prev.map((e) => (e.id === id ? previousEntry : e)));
      flash(err.response?.data?.message || "Erreur lors de la modification.");
    } finally {
      setPendingMutations((n) => Math.max(0, n - 1));
    }
  };
  const removeEntry = async (id) => {
    const ok = await dialog.confirm("Supprimer cette ligne de temps ?", { variant: "danger" });
    if (!ok) return;
    const previousEntries = entries;
    setEntries((prev) => prev.filter((e) => e.id !== id));
    setPendingMutations((n) => n + 1);
    try {
      await httpClient.delete(`/api/saisies-temps/${id}`);
      flash("Ligne supprimée");
    } catch (err) {
      setEntries(previousEntries);
      flash(err.response?.data?.message || "Erreur lors de la suppression.");
    } finally {
      setPendingMutations((n) => Math.max(0, n - 1));
    }
  };

  const openCloseOverlay = () => {
    if (pendingMutations > 0) {
      flash("Mise à jour en cours dans la base de données, veuillez patienter un instant puis réessayer.");
      return;
    }
    setNotifyUserIds([]);
    setCloseOverlayOpen(true);
  };

  const confirmClose = async () => {
    setClosing(true);
    try {
      const { data } = await httpClient.post("/api/jours-clotures", { date: dayISO, notifyUserIds });
      setEditIds(new Set());
      setCloseOverlayOpen(false);
      await fetchFeuilleTemps();
      // Le confetti "Journée clôturée !" ne se rejoue que lors de la toute
      // première clôture — un renvoi (journée déjà clôturée) n'affiche que
      // le toast d'envoi de mail.
      if (!data.alreadyClosed) {
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 2000);
      }

      if (notifyUserIds.length > 0) {
        if (data.emailSent) {
          flash(`Email envoyé à ${data.notifiedNames.join(", ")}`);
        } else {
          flash(data.emailError || "Erreur lors de l'envoi de l'email");
        }
      } else if (data.alreadyClosed) {
        flash("Journée déjà clôturée");
      }
    } catch (err) {
      flash(err.response?.data?.message || "Erreur lors de la clôture.");
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="w-full flex flex-col" style={{ minHeight: "calc(100vh - 120px)" }}>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs mb-3 flex-shrink-0 self-start" style={{ color: INK_3 }}>
          <ArrowLeft size={14} /> Retour au Bureau
        </button>
      )}

      <div className="mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
            Feuille de temps
          </h1>
          <button
            type="button"
            onClick={() => setShowTutorial(true)}
            title="Voir le tutoriel"
            className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
          >
            <HelpCircle size={14} />
          </button>
        </div>
        <p className="text-xs mt-0.5" style={{ color: INK_3 }}>
          Saisie journalière du temps · {utilisateur?.name} {utilisateur?.lastName}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4 flex-shrink-0">
        <button
          onClick={() => shiftDay(-1)}
          className="flex items-center justify-center"
          style={{ height: 32, width: 32, borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
        >
          <ChevronLeft size={15} />
        </button>
        <div className="relative">
          <button
            onClick={() => setShowCal((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold capitalize px-3"
            style={{ height: 32, borderRadius: 8, border: `1px solid ${LINE}`, color: INK }}
          >
            <CalendarDays size={13} /> {dayLabel}
          </button>
          {showCal && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowCal(false)} />
              <div className="absolute left-0 top-full mt-1 z-20" style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, boxShadow: "0 10px 28px rgba(0,0,0,.12)" }}>
                <MiniCalendar dayISO={dayISO} todayISO={todayIso()} entryDates={entryDates} closedDates={closedDates} onPick={pickDay} />
              </div>
            </>
          )}
        </div>
        <button
          onClick={() => pickDay(todayIso())}
          className="text-[11px] font-medium px-3"
          style={{ height: 32, borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
        >
          Aujourd'hui
        </button>
        <button
          onClick={() => shiftDay(1)}
          className="flex items-center justify-center"
          style={{ height: 32, width: 32, borderRadius: 8, border: `1px solid ${LINE}`, color: INK_2 }}
        >
          <ChevronRight size={15} />
        </button>

        {isLoading ? (
          <span className="inline-block animate-pulse" style={{ width: 74, height: 22, borderRadius: 20, background: NEUT_BG }} />
        ) : (
          <span
            className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1"
            style={{
              borderRadius: 20,
              background: closed ? OK_BG : isFuture ? INFO_BG : WARN_BG,
              color: closed ? OK_INK : isFuture ? INFO_INK : WARN_INK,
            }}
          >
            {closed && <Lock size={11} />} {closed ? "Clôturée" : isFuture ? "À venir" : "En cours"}
          </span>
        )}

        <span className="flex-1" />

        {isLoading ? (
          <span className="inline-block animate-pulse" style={{ width: 52, height: 24, borderRadius: 20, background: NEUT_BG }} />
        ) : (
          <span className="text-[13px] font-bold px-3 py-1" style={{ color: INK, background: BG, borderRadius: 20 }}>
            {dayTotal.toLocaleString("fr-FR")} h
          </span>
        )}
      </div>

      {error && (
        <p className="text-xs mb-2 flex-shrink-0" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      <div className="flex-1 min-h-0" style={{ border: `1px solid ${LINE}`, borderRadius: 16, background: "#fff", overflow: "hidden" }}>
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${LINE}` }}>
          {isLoading ? (
            <div className="flex flex-col gap-1.5 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8 }}>
                  <div className="h-3 rounded" style={{ width: 180, background: NEUT_BG }} />
                  <div className="h-3 rounded" style={{ width: 55, background: NEUT_BG }} />
                  <div className="h-3 rounded flex-1" style={{ background: NEUT_BG }} />
                </div>
              ))}
            </div>
          ) : dayEntries.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-center">
              <Clock size={22} style={{ color: INK_3 }} />
              <p className="text-xs mt-2" style={{ color: INK_3 }}>
                Aucune saisie pour cette journée.
              </p>
              {editable && (
                <p className="text-[11px] mt-1" style={{ color: INK_3 }}>
                  Ajoutez une première ligne ci-dessous.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {dayEntries.map((e) => {
                const rowEdit = editable && editIds.has(e.id);
                const d = drafts[e.id];
                return (
                  <div key={e.id} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8 }}>
                    {rowEdit ? (
                      <>
                        <div style={{ width: 180 }}>
                          <ClientPicker value={d.client} onChange={(c) => setDrafts((prev) => ({ ...prev, [e.id]: { ...prev[e.id], client: c } }))} />
                        </div>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={d.heures}
                          onChange={(ev) => setDrafts((prev) => ({ ...prev, [e.id]: { ...prev[e.id], heures: ev.target.value } }))}
                          className={fieldInput}
                          style={{ ...fieldStyle, width: 70 }}
                        />
                        <input
                          value={d.label}
                          onChange={(ev) => setDrafts((prev) => ({ ...prev, [e.id]: { ...prev[e.id], label: ev.target.value } }))}
                          className={`flex-1 ${fieldInput}`}
                          style={fieldStyle}
                        />
                        <button onClick={() => commitEdit(e.id)} className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: 6, color: OK_INK }}>
                          <Check size={14} />
                        </button>
                        <button onClick={() => cancelEdit(e.id)} className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: 6, color: INK_3 }}>
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-semibold truncate" style={{ color: INK, width: 180 }}>
                          {e.client?.nom || "— interne —"}
                        </span>
                        <span className="text-xs font-bold" style={{ color: BURGUNDY, width: 55 }}>
                          {e.heures.toLocaleString("fr-FR")} h
                        </span>
                        <span className="text-xs flex-1 truncate" style={{ color: INK_2 }}>
                          {e.label || "—"}
                        </span>
                        {editable ? (
                          <button onClick={() => startEdit(e)} title="Modifier" className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: 6, color: INK_3 }}>
                            <Pencil size={13} />
                          </button>
                        ) : (
                          <Lock size={13} style={{ color: INK_3 }} />
                        )}
                        {editable && (
                          <button onClick={() => removeEntry(e.id)} title="Supprimer" className="flex items-center justify-center flex-shrink-0" style={{ width: 26, height: 26, borderRadius: 6, color: BAD_INK }}>
                            <Trash2 size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!isLoading && editable && (
          <div className="px-4 py-3 flex items-end gap-2" style={{ background: BG, borderTop: `1px solid ${LINE}` }}>
            <div style={{ width: 180 }}>
              <label className="block text-[10.5px] font-semibold mb-1" style={{ color: INK_3 }}>
                Dossier
              </label>
              <ClientPicker value={newClient} onChange={setNewClient} />
            </div>
            <div style={{ width: 80 }}>
              <label className="block text-[10.5px] font-semibold mb-1" style={{ color: INK_3 }}>
                Heures
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={newHours}
                onChange={(e) => {
                  setNewHours(e.target.value);
                  if (fieldErrors.heures) setFieldErrors((prev) => ({ ...prev, heures: false }));
                }}
                onKeyDown={(e) => e.key === "Enter" && addEntry()}
                placeholder="0"
                className={`w-full ${fieldInput}`}
                style={fieldErrors.heures ? { ...fieldStyle, border: `1px solid ${BAD_INK}` } : fieldStyle}
              />
            </div>
            <div className="flex-1">
              <label className="block text-[10.5px] font-semibold mb-1" style={{ color: INK_3 }}>
                Description
              </label>
              <input
                value={newLabel}
                onChange={(e) => {
                  setNewLabel(e.target.value);
                  if (fieldErrors.label) setFieldErrors((prev) => ({ ...prev, label: false }));
                }}
                onKeyDown={(e) => e.key === "Enter" && addEntry()}
                placeholder="Décrivez la tâche effectuée…"
                className={`w-full ${fieldInput}`}
                style={fieldErrors.label ? { ...fieldStyle, border: `1px solid ${BAD_INK}` } : fieldStyle}
              />
            </div>
            <button
              onClick={addEntry}
              className="flex items-center gap-1.5 px-3 text-xs font-semibold text-white flex-shrink-0"
              style={{ background: BURGUNDY, borderRadius: 8, height: 36 }}
            >
              <Plus size={13} /> Ajouter
            </button>
          </div>
        )}

        {/* Reste affiché même une fois la journée clôturée : permet de
            renvoyer/notifier d'autres collaborateurs après coup (voir
            confirmClose/alreadyClosed) — seule la saisie (au-dessus) reste
            verrouillée. */}
        {!isLoading && !isFuture && (
          <div className="px-4 py-3 flex flex-col items-end gap-1.5" style={{ borderTop: `1px solid ${LINE}` }}>
            {closed && (
              <p className="text-[11px] m-0" style={{ color: INK_3 }}>
                Cette feuille de temps a déjà été clôturée.
              </p>
            )}
            <button
              onClick={openCloseOverlay}
              disabled={dayEntries.length === 0}
              title={dayEntries.length === 0 ? "Ajoutez au moins une saisie pour pouvoir clôturer" : undefined}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50 flex-shrink-0"
              style={{ background: BURGUNDY, borderRadius: 10 }}
            >
              <Lock size={14} /> {closed ? "Renvoyer / notifier" : "Clôturer la journée"}
            </button>
          </div>
        )}
      </div>

      {closeOverlayOpen && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center p-4"
          style={{ background: "rgba(31,22,32,.45)" }}
          onClick={() => !closing && setCloseOverlayOpen(false)}
        >
          <div
            className="w-full flex flex-col overflow-hidden"
            style={{ maxWidth: 460, background: "#fff", borderRadius: 18, boxShadow: "0 24px 60px rgba(20,14,24,.30)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3.5 px-5 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 40, height: 40, borderRadius: 10, background: BURGUNDY_TINT, color: BURGUNDY }}
              >
                <Lock size={19} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold m-0" style={{ color: INK }}>
                  {closed ? "Renvoyer la feuille de temps" : "Clôturer la journée"}
                </h2>
                <div className="text-[11px] mt-0.5 capitalize" style={{ color: INK_3 }}>
                  {dayLabel} · {dayTotal.toLocaleString("fr-FR")} h
                </div>
              </div>
              <button
                onClick={() => setCloseOverlayOpen(false)}
                disabled={closing}
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${LINE}` }}
              >
                <X size={14} style={{ color: INK_2 }} />
              </button>
            </div>

            <div className="px-5 py-4">
              {closed && (
                <p className="text-xs mb-3 px-3 py-2" style={{ color: INK_2, background: WARN_BG, borderRadius: 8 }}>
                  Cette journée est déjà clôturée — vous pouvez renvoyer/notifier d'autres collaborateurs.
                </p>
              )}
              <p className="text-xs mb-3" style={{ color: INK_2 }}>
                Choisissez un ou plusieurs collaborateurs à qui envoyer votre feuille de temps de cette journée (facultatif) :
              </p>
              <UserSearchTagPicker
                allUsers={allUsers}
                label="Personne(s) à notifier"
                selectedIds={notifyUserIds}
                onAdd={(user) => setNotifyUserIds((ids) => [...ids, user.id])}
                onRemove={(id) => setNotifyUserIds((ids) => ids.filter((i) => i !== id))}
                emptyText={closed ? "Aucune personne ajoutée." : "Aucune personne ajoutée — la clôture se fera sans envoi de mail."}
              />
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3.5" style={{ borderTop: `1px solid ${LINE}`, background: "#FAFAF8" }}>
              <button
                onClick={() => setCloseOverlayOpen(false)}
                disabled={closing}
                className="px-4 py-2 text-xs font-semibold disabled:opacity-50"
                style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK, background: "#fff" }}
              >
                Annuler
              </button>
              <button
                onClick={confirmClose}
                disabled={closing}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                style={{ background: BURGUNDY, borderRadius: 10 }}
              >
                {notifyUserIds.length > 0 ? <Send size={13} /> : <Lock size={13} />}
                {closing ? "Envoi…" : closed ? "Renvoyer" : "Clôturer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {celebrate && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: "rgba(31,22,32,.55)" }}>
          <style>{`
            @keyframes ftConfettiFall {
              0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
              100% { transform: translateY(220px) rotate(360deg); opacity: 0; }
            }
          `}</style>
          <div className="relative flex flex-col items-center text-center px-10 py-10" style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 60px rgba(20,14,24,.30)", overflow: "hidden", minWidth: 300 }}>
            {Array.from({ length: 14 }, (_, i) => (
              <span
                key={i}
                className="absolute"
                style={{
                  top: 0,
                  left: `${6 + i * 6.5}%`,
                  width: 8,
                  height: 8,
                  borderRadius: 2,
                  background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                  animation: `ftConfettiFall 1.6s ease-in ${i * 0.06}s forwards`,
                }}
              />
            ))}
            <div className="flex items-center justify-center mb-3" style={{ width: 56, height: 56, borderRadius: "50%", background: OK_BG, color: OK_INK }}>
              <Check size={28} />
            </div>
            <h2 className="text-lg font-semibold m-0" style={{ color: INK }}>
              Journée clôturée !
            </h2>
            <p className="text-sm mt-1 capitalize" style={{ color: INK_3 }}>
              {dayTotal.toLocaleString("fr-FR")} h · {dayLabel}
            </p>
          </div>
        </div>
      )}

      {showTutorial && (
        <TutorialOverlay steps={feuilleTempsTutorial} onClose={() => setShowTutorial(false)} />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 text-xs px-4 py-2.5 text-white z-[220]"
          style={{ background: INK, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.24)" }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default FeuilleTemps;
